import { useState, useEffect } from 'react';
import axios from 'axios';
import { createGlobalStyle } from 'styled-components';
import './RankingReport.css';
import { useAuthContext } from '../../context/auth-context';
import { baseUrls, reportUrls } from '../../constants/module-constants';
import { getAffiliationZones, filterByAffiliationZone } from '../../utils/report-filters';

const PrintStyles = createGlobalStyle`
  @media print {
    body * {
      visibility: hidden;
    }

    .ranking-content,
    .ranking-content * {
      visibility: visible;
    }

    .ranking-content {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 0 10px;
    }

    @page {
      size: landscape;
      margin: 0.5cm;
    }
  }
`;

const RankingReport = () => {
  const { activeCamp, effectiveZone, hasPermission } = useAuthContext();
  // Esta página pinta un <select> propio (va con estilos de impresión), así que la
  // regla de `AffiliationFilter` se repite aquí: al Secretario de Zona el servidor ya
  // le devuelve solo su zona y ofrecerle "Todas" sería mentira.
  // `effectiveZone` incluye la zona de la vista previa: ver AffiliationFilter.
  const zonaFija = Boolean(effectiveZone) && !hasPermission('reports.view-all-zones');
  const [rankingData, setRankingData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [error, setError] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');

  useEffect(() => {
    setRankingData(null);
    setCategories([]);
    setError('');
    setLoadingStatus('');
    setZoneFilter('');
  }, [activeCamp?._id]);

  const fetchRankingReport = async () => {
    if (!activeCamp?._id) {
      setError('Seleccione un campamento activo');
      return;
    }

    setLoading(true);
    setError('');
    setRankingData(null);

    try {
      setLoadingStatus('Recalculando calificaciones...');
      await axios.post(`${baseUrls.baseUrl}/grade/sum_grades`, { campId: activeCamp._id });

      setLoadingStatus('Generando ranking...');
      const response = await axios.get(`${reportUrls.RANKING}/${activeCamp._id}`);
      setRankingData(response.data.data);
      setCategories(response.data.data.categories || []);
    } catch (err) {
      console.error('Error fetching ranking:', err);
      setError('Error al generar el ranking');
    } finally {
      setLoading(false);
      setLoadingStatus('');
    }
  };

  const getCategoryClass = (categoryIndex) => `ranking-tier-${Math.min(categoryIndex ?? 0, 5)}`;

  const allRankings = rankingData?.rankings || [];
  const zones = getAffiliationZones(allRankings);
  // La posición se mantiene global: es el ranking del campamento, no de la zona
  const visibleRankings = filterByAffiliationZone(allRankings, zoneFilter);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {rankingData && <PrintStyles />}
      <div className="ranking-report">
        <div className="report-header no-print">
          <h1>Ranking General de Clubes</h1>

          <div className="filters">
            <button
              onClick={fetchRankingReport}
              disabled={loading || !activeCamp?._id}
              className="btn-primary"
            >
              {loading ? loadingStatus || 'Cargando...' : 'Generar Ranking'}
            </button>

            {rankingData && zones.length > 0 && (
              <div className="filter-group">
                <label htmlFor="ranking-zone">Afiliación (Zona)</label>
                <select
                  id="ranking-zone"
                  value={zonaFija ? effectiveZone : zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                  disabled={zonaFija}
                >
                  {!zonaFija && <option value="">Todas</option>}
                  {(zonaFija ? [effectiveZone] : zones).map((zone) => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </select>
              </div>
            )}

            {rankingData && (
              <button onClick={handlePrint} className="btn-secondary">
                Imprimir
              </button>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
        </div>

        {rankingData && (
          <div className="ranking-content">
            <div className="print-header">
              <h2>Ranking General de Clubes</h2>
              <p className="camp-name">{activeCamp?.name}</p>
              <p className="max-points">
                Puntos Máximos Posibles: <strong>{rankingData.totalMaxPoints}</strong>
              </p>
              {zoneFilter && (
                <p className="max-points">
                  Afiliación: <strong>{zoneFilter}</strong> — {visibleRankings.length} de {allRankings.length} clubes
                  {' '}(la posición es la del campamento completo)
                </p>
              )}
            </div>

            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="ranking-table">
                <thead>
                  <tr>
                    <th>Posición</th>
                    <th>Club</th>
                    <th>Afiliación</th>
                    <th>Director</th>
                    <th>Puntos</th>
                    <th>Porcentaje</th>
                    <th>Clasificación</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRankings.map((club) => (
                    <tr key={club.clubId} className={getCategoryClass(club.categoryIndex)}>
                      <td className="position">
                        <span className="position-badge">{club.position}</span>
                      </td>
                      <td className="club-name">{club.clubName}</td>
                      <td>{club.affiliationZone || '-'}</td>
                      <td>{club.director}</td>
                      <td className="points">
                        {club.totalPoints} / {club.maxPoints}
                      </td>
                      <td className="percentage">{club.percentage}%</td>
                      <td className="ranking">
                        <span className="ranking-text">{club.ranking}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ranking-legend">
              <h3>Clasificación</h3>
              <ul>
                {categories.map((cat, index) => (
                  <li key={index} className={`ranking-tier-${Math.min(index, 5)}`}>
                    {cat.name}: {cat.minPercentage}%{index === 0 ? ' - 100%' : index < categories.length - 1 ? ` - ${categories[index - 1].minPercentage - 1}%` : ' o menos'}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RankingReport;
